import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { EUROPEAN_COUNTRY_CODES } from '../constants/europeanCountries'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const QUICKBOX_MESSAGES_REFRESH_EVENT = 'quickbox-messages-refresh'

const VehicleUploadModal = ({ open, onClose, onSaved }) => {
  const { t, i18n } = useTranslation()

  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState(null)
  const [saved, setSaved] = useState(false)

  const [registrationCountry, setRegistrationCountry] = useState('SK')
  const [countryError, setCountryError] = useState(false)

  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState(false)

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  useEffect(() => {
    if (!open) {
      setMessage(null)
      setFile(null)
      setFileError(false)
      setRegistrationCountry('SK')
      setCountryError(false)
      setHoverBtn(null)
      setPressedBtn(null)
      setSaved(false)
    }
  }, [open])

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation()
      onClose()
    }
  }

  window.addEventListener('keydown', handleKeyDown)
  return () => window.removeEventListener('keydown', handleKeyDown)
}, [open, onClose])

  const getCountryLabel = (code) => {
    if (!code) return ''
    const name = t(code)
    const resolved = name && name !== code ? name : code
    return `${code} - ${resolved}`
  }

  const handleFileSelected = (e) => {
    const f = e.target.files?.[0]
    if (!f) return

    setFileError(false)
    setMessage(null)
    setFile(f)
  }

  const saveUpload = async () => {
    let hasError = false

    if (!file) {
      setFileError(true)
      hasError = true
    }

    if (!registrationCountry) {
      setCountryError(true)
      hasError = true
    }

    if (hasError) return

    try {
      setUploading(true)
      setMessage(null)

      const fd = new FormData()
      fd.append('file', file)
      fd.append('registrationCountry', registrationCountry)

const res = await fetchWithAuth(
  '/vehicle/upload',
  {
    method: 'POST',
    body: fd
  },
  i18n.language
)

      if (!res.ok) {
        const err = await res.json().catch(() => null)
        setMessage({
          type: 'error',
          message: err?.message || err?.error || t('vehicle_save_failed')
        })
        return
      }

      setMessage({
        type: 'success',
        message: t('TRIPLOG.VEHICLES.UPLOAD_SUCCESS_MSG')
      })

      setSaved(true)
      setTimeout(() => {
        window.dispatchEvent(new Event(QUICKBOX_MESSAGES_REFRESH_EVENT))
      }, 2000)

    } catch {
      setMessage({
        type: 'error',
        message: t('CONNECTION_ERROR')
      })
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  const baseButtonColor = fileError ? '#f6caca' : '#f5f5f5'

  return (
    <>
      <Overlay open={uploading} zIndex={4000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4000
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: 20,
            border: '2px solid #107c6b',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: 'clamp(320px, 30vw, 420px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >

          <h3 style={{ fontSize: 20, marginBottom: 16, textAlign: 'center' }}>
            {t('vehicle_registration_country_label')}
          </h3>

          <select
            disabled={saved}
            value={registrationCountry}
            onChange={(e) => {
              setRegistrationCountry(e.target.value)
              setCountryError(false)
            }}
            style={{
              width: 240,
              height: 34,
              borderRadius: 6,
              border: countryError ? '1px solid #c33' : '1px solid #999',
              backgroundColor: countryError ? '#f6caca' : '#ffffff',
              padding: '0 8px',
              marginBottom: 18
            }}
          >

            {['SK', ...EUROPEAN_COUNTRY_CODES.filter(c => c !== 'SK')].map(code => (
              <option
                key={code}
                value={code}
                style={{ backgroundColor: '#ffffff', color: '#000' }}
              >
                {getCountryLabel(code)}
              </option>
            ))}
          </select>

          <input
            id="vehicle-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileSelected}
            style={{ display: 'none' }}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16
            }}
          >
<button
  disabled={saved}
  onClick={() => {
    if (saved) return
    document.getElementById('vehicle-upload-input').click()
  }}
  onMouseEnter={(e) => {
    if (saved) return
    e.currentTarget.style.background = '#9ad1bb'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = baseButtonColor
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    if (saved) return
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
    e.currentTarget.blur()
  }}
  style={{
    border: '1.5px solid #107c6b',
    background: baseButtonColor,
    borderRadius: 6,
    height: 28,
    padding: '0 16px',
    cursor: saved ? 'default' : 'pointer',
    fontWeight: 700,
    boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
    transition: 'transform 0.05s ease, box-shadow 0.1s ease',
    outline: 'none',
    opacity: saved ? 0.6 : 1
  }}
>
  {t('vehicle_select_file')}
</button>

            <span
              style={{
                fontSize: 14,
                color: file ? '#107c6b' : '#888'
              }}
            >
              {file ? file.name : t('vehicle_no_file')}
            </span>
          </div>

          {message && (
            <div style={{ width: '100%', marginBottom: 14 }}>
              <MessageBox message={message.message} type={message.type} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>

            {!saved && (
              <>
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'saveUpload',
                    hoverBtn === 'saveUpload'
                  )}
                  onMouseEnter={() => setHoverBtn('saveUpload')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('saveUpload')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={saveUpload}
                >
                  {t('SAVE')}
                </button>

                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    'cancelUpload',
                    hoverBtn === 'cancelUpload'
                  )}
                  onMouseEnter={() => setHoverBtn('cancelUpload')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('cancelUpload')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={onClose}
                >
                  {t('CANCEL')}
                </button>
              </>
            )}

            {saved && (
              <button
                style={modalPrimaryButtonStyle(
                  pressedBtn,
                  'okUpload',
                  hoverBtn === 'okUpload'
                )}
                onMouseEnter={() => setHoverBtn('okUpload')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('okUpload')}
                onMouseUp={() => setPressedBtn(null)}
                onClick={() => {
                  onClose()
                  onSaved?.()
                }}
              >
                OK
              </button>
            )}

          </div>

        </div>
      </div>
    </>
  )
}

export default VehicleUploadModal